import { SUBJECTS, type SchoolLevel, type SubjectSlug } from '@jeraisonne/db';

export type DemoPhase =
  | 'level_selection'
  | 'subject_selection'
  | 'warmup'
  | 'exercise'
  | 'signup_paywall';

export type DemoChoice = {
  id: string;
  label_md: string;
  is_correct: boolean;
  feedback_md?: string;
};

export type DemoQuestion = {
  id: string;
  prompt_md: string;
  explanation_md: string;
  choices: DemoChoice[];
};

export type DemoStep = {
  id: string;
  prompt_md: string;
  explanation_md: string;
  choices: DemoChoice[];
};

/** Difficulté d'un exercice — miroir de l'enum `exercise_difficulty`. */
export type DemoDifficulty = 'easy' | 'medium' | 'hard';

export type DemoExercise = {
  title: string;
  chapter_label: string;
  steps: DemoStep[];
  /**
   * Habillage optionnel pour reproduire le design d'un exercice réel.
   * Renseigné par `get_exercise_demo` (aperçu d'un exercice publié) ;
   * absent du parcours `/demo` guidé (contenu synthétique).
   */
  cover_url?: string | null;
  subject?: SubjectSlug | null;
  level?: SchoolLevel | null;
  context_md?: string | null;
  statement_md?: string | null;
  difficulty?: DemoDifficulty | null;
  estimated_minutes?: number | null;
};

export type DemoContent = {
  level: SchoolLevel;
  warmup: DemoQuestion[];
  exercise: DemoExercise;
  source: 'db' | 'fallback';
};

export type DemoAnswer = {
  questionId: string;
  choiceId: string;
  wasCorrect: boolean;
};

export type DemoState = {
  selectedLevel: SchoolLevel | null;
  selectedSubject: SubjectSlug | null;
  phase: DemoPhase;
  warmupAnswers: DemoAnswer[];
  exerciseAnswers: DemoAnswer[];
  startedAt: string;
};

/** Une matière proposée dans l'écran de sélection démo. */
export type DemoSubject = {
  value: SubjectSlug;
  label: string;
  emoji: string;
};

/** Matière par défaut quand un niveau n'a aucune matière rattachée. */
export const DEFAULT_DEMO_SUBJECT: DemoSubject = {
  value: 'math',
  label: 'Mathématiques',
  emoji: '📐',
};

/**
 * Matières rattachées à un niveau (curriculum `@jeraisonne/db`).
 * Si aucune matière n'est disponible pour le niveau, on retombe sur
 * Mathématiques par défaut — la démo propose toujours au moins un choix.
 */
export function subjectsForLevel(level: SchoolLevel): DemoSubject[] {
  const matches = SUBJECTS.filter((s) => s.levels.includes(level)).map((s) => ({
    value: s.value,
    label: s.label,
    emoji: s.emoji,
  }));
  return matches.length > 0 ? matches : [DEFAULT_DEMO_SUBJECT];
}

export const LEVEL_LABEL: Record<SchoolLevel, string> = {
  '6e': '6e',
  '5e': '5e',
  '4e': '4e',
  '3e': '3e',
  '2nde': '2nde',
  '1re': '1re',
  tle: 'Terminale',
  mpsi: 'MPSI',
  ptsi: 'PTSI',
  mp: 'MP',
  pt: 'PT',
};

export const DEMO_LEVELS: SchoolLevel[] = ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'];

export const MAX_WARMUP_QUESTIONS = 5;
export const MAX_EXERCISE_STEPS = 5;
